import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader } from "~/components/PageHeader";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";
import { usePaginatedWatchList } from "~/lib/tanstack-query/hooks/useWatchlist";
import { PaginationBar } from "~/components/ui/pagination-bar";
import { LocationCard } from "~/components/Location/LocationCard";
import { useRemoveLocationFromWatchListMutation } from "~/lib/tanstack-query/hooks/mutations/watchlist";
import { Button } from "~/components/ui/button";
import { TrashIcon } from "~/icons/TrashIcon";
import { useSetInquiryLocationMutation } from "~/lib/tanstack-query/hooks/mutations/inquiryLocations";
import { openInquiry, setInquirySelectedLocations } from "~/lib/stores/inquiry";
import { useStore } from "@tanstack/react-store";
import { InquiryStore } from "~/lib/stores/inquiry";

export const Route = createFileRoute("/dashboard/watchlist")({
  component: RouteComponent,
});

function RouteComponent() {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const { data: paginatedWatchList, isPending: isPendingPaginatedWatchList } =
    usePaginatedWatchList(currentPage, pageSize);
  const removeLocationFromWatchListMutation =
    useRemoveLocationFromWatchListMutation();
  const { mutateAsync: setInquiryLocation } = useSetInquiryLocationMutation();
  const selectedLocationIds = useStore(
    InquiryStore,
    (state) => state.selectedLocationIds,
  );

  const watchList = paginatedWatchList?.locations ?? [];

  const handleSingleInquiry = async (locationId: number) => {
    await setInquiryLocation(locationId);
    openInquiry("direct", [locationId]);
  };

  const handleMultiInquiry = (locationId: number) => {
    // Check if location is already selected
    const isSelected = selectedLocationIds.includes(locationId);

    if (!isSelected) {
      // Check if we can add more (max 10)
      if (selectedLocationIds.length >= 10) {
        return; // Max 10 selections
      }
      // Add location to selection
      setInquirySelectedLocations([...selectedLocationIds, locationId]);
    }

    // Open inquiry modal in watchlist mode with updated selection
    const updatedSelection = isSelected
      ? selectedLocationIds
      : [...selectedLocationIds, locationId];
    openInquiry("watchlist", updatedSelection);
  };

  return (
    <div className="w-full mb-6">
      <PageHeader title="Meine Merkliste" />
      <div className="flex flex-col gap-2">
        <p>
          Mit dem Button "Mehrfachanfrage" kannst du bis zu 10 Anfragen über
          Auswahl in der Merkliste gleichzeitig stellen.
        </p>
        <p>
          Mit dem Button "Einzelanfrage" stellst du deine Anfrage
          ausschließlich an diese/n Veranstaltungsfläche-/raum.
        </p>
      </div>
      <div className="mt-12 grid gap-8">
        {isPendingPaginatedWatchList ? (
          <div>Loading...</div>
        ) : (
          watchList.map((location) => (
            <div
              key={location.id}
              className="flex flex-col lg:flex-row gap-4 justify-between items-end"
            >
              <LocationCard
                location={location}
                compact
                isBookmarked={true}
                onBookmark={() => {
                  removeLocationFromWatchListMutation.mutateAsync(location.id);
                }}
              />
              <div className="flex flex-col gap-3 items-end">
                <div className="flex gap-3">
                  <Button
                    size={"icon"}
                    variant="outline"
                    title="Aus der Merkliste entfernen"
                    onClick={() =>
                      removeLocationFromWatchListMutation.mutate(location.id)
                    }
                    disabled={removeLocationFromWatchListMutation.isPending}
                    className="size-11"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </Button>
                  <Button
                    size={"lg"}
                    variant="black"
                    asChild
                    className="w-[140px]"
                  >
                    <Link to={withLocale(getLocationPath(location))}>Details</Link>
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button
                    size={"lg"}
                    variant="default"
                    className="w-[140px]"
                    onClick={() => handleMultiInquiry(location.id)}
                  >
                    Mehrfachanfrage
                  </Button>
                  <Button
                    size={"lg"}
                    variant="outline"
                    className="w-[140px]"
                    onClick={() => handleSingleInquiry(location.id)}
                  >
                    Einzelanfrage
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
        <PaginationBar
          currentPage={currentPage}
          totalPages={paginatedWatchList?.totalPages ?? 0}
          onPageChange={setCurrentPage}
          className="my-8"
          compact={false}
        />
      </div>
    </div>
  );
}
